import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { map } from 'rxjs/operators';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';

import {
    AFormat,
    BoxLevel,
    LessonPrivilegeSetting,
    MinifiedBox,
    ShortDataLesson,
    ExExercise,
    StudentClass,
    Profile
} from '@df/interfaces';

import { LessonService } from '@app/teacher/services/lesson.service';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

import { COPY_ACTION, DELETE_ACTION, LESSON_CANCELED, LESSON_CREATED, DEFAULT_ACCESS_RIGHTS } from '@app/app.constants';
import { Observable } from 'rxjs';
import { PrivilegeService } from '@app/core/services/privilege.service';
import { ExExerciseService } from '@app/core/services/ex_exercise.service';

import { printIcon } from '@app/app.constants';
import { copyIcon } from '@app/app.constants';
import { removeIcon } from '@app/app.constants';
import { refreshIcon } from '@app/app.constants';
import { armIcon } from '@app/app.constants';
import { CopyFormComponent } from '@app/teacher/components/prepare/copy-form/copy-form.component';
import { MatDialog } from '@angular/material/dialog';
import { DeleteFormComponent } from '@app/teacher/components/prepare/delete-form/delete-form.component';
import { PeopleService } from '@app/core/services';
import { AccountService } from '@app/teacher/services/account.service';

@Component({
    selector: 'app-edit-lesson',
    templateUrl: './edit-lesson.component.html',
    styleUrls: ['./edit-lesson.component.scss']
})
export class EditLessonComponent implements OnInit, OnDestroy {
    @ViewChild('printNotAllowedModal', { static: true }) unpreparedModal: TemplateRef<any>;

    lesson: ShortDataLesson;
    lessonBoxes: Array<MinifiedBox>;
    lessonBoxLevels: Array<BoxLevel>;
    isBoxesLoaded = false;
    modalRef: BsModalRef;
    selectedClassIDForCopy: number;
    teacherClasses: StudentClass[];
    allowPrint = false;
    toBeGeneratedBoxes: MinifiedBox[] = [];
    exExercise: ExExercise;

    printIcon = printIcon;
    copyIcon = copyIcon;
    removeIcon = removeIcon;
    refreshIcon = refreshIcon;
    armIcon = armIcon;

    privilegeSettings: LessonPrivilegeSetting[] = [];
    isAccedEdit = false;
    BOXES_STATE_INTERVAL = 4000;
    profile: Profile;

    groupRights = DEFAULT_ACCESS_RIGHTS;

    private stopInterval;
    private defaultCardFormat = AFormat.A7_FORMAT;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private activeRoute: ActivatedRoute,
        private lessonService: LessonService,
        private peopleService: PeopleService,
        private accountService: AccountService,
        public dialog: MatDialog,
        private exExerciseService: ExExerciseService,
        private modalService: BsModalService,
        private privilegeService: PrivilegeService,
        private router: Router
    ) {}

    public ngOnInit(): void {
        const lessonID = this.activeRoute.snapshot.params['lesson_id'];

        this.accountService.getProfileData().subscribe(response => {
            if (response.data && response.data.user.username) {
                this.profile = response.data;
                this.checkAccess();
            }
        });

        this.getLessonExExercise(lessonID);

        forkJoin(this.getShortDataLesson(lessonID), this.fillLessonBoxes(lessonID), this.getTeacherClasses()).subscribe(
            () => {
                this.getLessonPrivilegeSettings();
                this.checkForPrint();
                this.pingBoxesState(lessonID);
                // this.checkLessonToTemplate();
            }
        );
    }

    public ngOnDestroy(): void {
        clearInterval(this.stopInterval);
        this.destroy$.next(true);
    }

    openRemoveDialog(): void {
        const dialogRef = this.dialog.open(DeleteFormComponent, {
            width: '630px',
            height: '320px',
            data: {
                lesson: this.lesson,
                classTitle: this.lesson.class_title
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.onModalSubmit();
        });
    }

    openCopyDialog(): void {
        const dialogRef = this.dialog.open(CopyFormComponent, {
            width: '630px',
            height: '620px',
            data: {
                lesson: this.lesson,
                classTitle: this.lesson.class_title,
                classID: this.lesson.class_id,
                teacherClasses: this.teacherClasses
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.onModalSubmit();
        });
    }

    // private checkLessonToTemplate(): void {
    //     const includeTemplateWord = this.lesson.class_title.includes('Шаблон');
    //     if (includeTemplateWord && !this.groupRights.edit_templates.is_access) {
    //         this.groupRights.copy_other_lesson.is_access = false;
    //         this.groupRights.delete_other_lesson.is_access = false;
    //         this.groupRights.edit_other_lesson.is_access = false;
    //     }

    //     if (includeTemplateWord && this.groupRights.edit_templates.is_access) {
    //         this.groupRights.copy_other_lesson.is_access = true;
    //         this.groupRights.edit_other_lesson.is_access = true;
    //     }
    // }

    private getShortDataLesson(lessonID: number): Observable<any> {
        return this.lessonService
            .getShortDataLesson(lessonID)
            .pipe(takeUntil(this.destroy$))
            .pipe(
                map((next: any) => {
                    this.lesson = next;
                    this.selectedClassIDForCopy = this.lesson.class_id;
                    this.isAccedEdit = next.status === LESSON_CANCELED || next.status === LESSON_CREATED;
                    return next;
                })
            );
    }

    private fillLessonBoxes(lessonID: number): Observable<any> {
        return this.lessonService
            .getLessonBoxes(lessonID)
            .pipe(takeUntil(this.destroy$))
            .pipe(
                map((next: any) => {
                    this.lessonBoxes = next['boxes'];
                    this.lessonBoxLevels = next['box_levels'];
                    this.isBoxesLoaded = true;
                    return next;
                })
            );
    }

    private pingBoxesState(lessonID: number) {
        this.stopInterval = setInterval(() => {
            this.lessonService
                .pingBoxesState(lessonID)
                .pipe(takeUntil(this.destroy$))
                .subscribe(next => {
                    this.toBeGeneratedBoxes = [];
                    this.updateBoxStatus(next);
                });
        }, this.BOXES_STATE_INTERVAL);
    }

    private updateBoxStatus(boxToBeGeneratedList: number[]) {
        if (boxToBeGeneratedList.length !== 0) {
            this.allowPrint = false;
            for (const box of this.lessonBoxes) {
                if (boxToBeGeneratedList.indexOf(box.id) === -1) {
                    box.generated = true;
                } else {
                    this.toBeGeneratedBoxes.push(box);
                    box.generated = false;
                }
            }
        } else {
            clearInterval(this.stopInterval);
            this.allowPrint = true;
            this.lessonBoxes.forEach((box: MinifiedBox) => {
                box.generated = true;
            });
        }
    }

    private checkForPrint() {
        for (const box of this.lessonBoxes) {
            if (!box.generated) {
                this.allowPrint = false;
                break;
            } else {
                this.allowPrint = true;
            }
        }
        for (const box of this.lessonBoxes) {
            if (!box.generated) {
                this.toBeGeneratedBoxes.push(box);
            }
        }
    }

    public getTeacherClasses() {
        return this.peopleService
            .getCurrentUserClasses()
            .pipe(takeUntil(this.destroy$))
            .pipe(
                map(resp => {
                    this.teacherClasses = resp.data;
                    return resp.data;
                })
            );
    }

    public addNewBox(): void {
        if (this.isAccedEdit) {
            const title = Number(this.lessonBoxes.length);
            let lessonID = 0;
            if (this.lesson) {
                lessonID = this.lesson.id;
            }

            const newBox = <MinifiedBox>{
                id: null,
                title: title,
                level: this.lessonBoxLevels[0].id,
                number_of_cards: 0,
                exercises_in_card: 0,
                lesson_id: lessonID,
                cost: 0,
                files: [],
                cost_no_risk: 0,
                reward_no_risk: this.lessonBoxLevels[0].level,
                cost_with_risk: 0,
                reward_with_risk: 0,
                penalty_with_risk: 0,
                additional_attempts: 0,
                check_character_id: null,
                risk_character_id: null,
                sell_character_id: null,
                cards_format: this.defaultCardFormat,
                generated: false,
                smart_sprints: []
            };

            this.lessonService
                .createNewLessonBox(newBox)
                .pipe(takeUntil(this.destroy$))
                .pipe(
                    map((next: any) => {
                        return next.response;
                    })
                )
                .subscribe((newCreatedBox: MinifiedBox) => {
                    this.lessonBoxes.push(newCreatedBox);
                    this.getLessonPrivilegeSettings();
                    this.checkForPrint();
                });
        }
    }

    public handleCardAction($event): void {
        const cardAction = $event;

        switch (cardAction.command) {
            case COPY_ACTION:
                this.copyCard(cardAction.cardIndex);
                break;

            case DELETE_ACTION:
                this.deleteCard(cardAction.cardIndex);
                break;

            default:
                console.log('Command undefined');
        }
    }

    public copyCard(cardIndex): void {
        this.getShortDataLesson(this.lesson.id);
        const lessonBoxForCopy = this.lessonBoxes[cardIndex];
        const nextFromSelectedIndex = cardIndex + 1;
        lessonBoxForCopy.title = nextFromSelectedIndex;
        this.lessonService
            .createNewLessonBox(lessonBoxForCopy)
            .pipe(takeUntil(this.destroy$))
            .pipe(
                map((next: any) => {
                    return next.response;
                })
            )
            .subscribe((newCopiedBox: MinifiedBox) => {
                this.lessonBoxes.splice(nextFromSelectedIndex, 0, newCopiedBox);
                this.getLessonPrivilegeSettings();
                this.checkForPrint();
            });
    }

    public deleteCard(cardIndex): void {
        this.getShortDataLesson(this.lesson.id);
        if (this.isAccedEdit) {
            const lessonBoxForDelete = this.lessonBoxes[cardIndex];
            this.lessonBoxes.splice(cardIndex, 1);
            this.lessonService.deleteLessonBox(lessonBoxForDelete.id).subscribe(
                () => {
                    this.checkForPrint();
                },
                error => {
                    console.warn(`Бокс ${cardIndex + 1} с ID: ${lessonBoxForDelete.id} не был удален!`);
                    console.error(`Причина: ${error}`);
                    this.lessonBoxes.splice(cardIndex, 0, lessonBoxForDelete);
                }
            );
        }
    }

    public printLesson(): void {
        if (this.allowPrint) {
            this.router.navigate(['/teacher/print', this.lesson.id]);
        } else {
            this.openModal(this.unpreparedModal, { class: 'modal-unprepared' });
        }
    }

    public checkCopyAccess(): boolean {
        if (this.groupRights.copy_other_lesson.is_access) {
            if (this.lesson && this.lesson.class_title.includes('Шаблон')) {
                return this.groupRights.copy_templates.is_access;
            }
            return true;
        }
        return false;
    }

    private openModal(template: TemplateRef<any>, config = {}): void {
        this.modalRef = this.modalService.show(template, config);
    }

    public onModalSubmit(): void {
        this.router.navigate(['/teacher/prepare']);
    }

    public getLessonPrivilegeSettings(): void {
        this.privilegeService.getLessonPrivilegeSettings(this.lesson.id).subscribe(resp => {
            if (resp.data.length) {
                this.privilegeSettings = resp.data as LessonPrivilegeSetting[];
            }
        });
    }

    private getLessonExExercise(lessonId: number) {
        this.exExerciseService.getLessonExExercise(lessonId).subscribe(resp => {
            this.exExercise = resp;
        });
    }

    private checkAccess(): void {
        const user_rights = this.profile.group.rights;

        user_rights.forEach(item => {
            switch (+item.index_title) {
                // доступ к изменению статуса урока
                case this.groupRights.change_status_other_lesson.index_title:
                    this.groupRights.change_status_other_lesson.is_access = true;
                    break;
                // доступ к копированию чужих уроков
                case this.groupRights.copy_other_lesson.index_title:
                    this.groupRights.copy_other_lesson.is_access = true;
                    break;
                // доступ к созданию урока
                case this.groupRights.create_lesson.index_title:
                    this.groupRights.create_lesson.is_access = true;
                    break;
                // доступ к удалению чужого урока
                case this.groupRights.delete_other_lesson.index_title:
                    this.groupRights.delete_other_lesson.is_access = true;
                    break;
                // доступ к редактированию чужого урока
                case this.groupRights.edit_other_lesson.index_title:
                    this.groupRights.edit_other_lesson.is_access = true;
                    break;
                // доступ к редактированию шаблонов
                case this.groupRights.edit_templates.index_title:
                    this.groupRights.edit_templates.is_access = true;
                    break;
                // доступ к копированию шаблонов
                case this.groupRights.copy_templates.index_title:
                    this.groupRights.copy_templates.is_access = true;
                    break;
                // доступ к удалению шаблонов
                case this.groupRights.delete_templates.index_title:
                    this.groupRights.delete_templates.is_access = true;
                    break;
                default:
                    break;
            }
        });
    }
}
